/**
 * Wizard shell — renders the left-side stepper and the active step panel.
 * Each step is a self-contained screen that reads `wizard` state and calls
 * `onAdvance` when its completion criteria are satisfied.
 */
import type { WizardState, WizardStep } from '../api';
import { Pill } from '../ui/primitives';

const STEP_LABELS: Record<WizardStep, { title: string; description: string }> = {
  self: {
    title: '1. Self',
    description: 'Your own party — used for tracking your P&L.',
  },
  uplines: {
    title: '2. Uplines',
    description: 'Supplier parties. SRB for most users.',
  },
  clients: {
    title: '3. Clients',
    description: 'POCs who lease accounts from you.',
  },
  accounts: {
    title: '4. Accounts',
    description: 'Accounts, their aliases, and active assignments.',
  },
  opening_balances: {
    title: '5. Opening balances',
    description: 'Carry-over balances from your old workflow.',
  },
  done: {
    title: 'Done',
    description: 'Ready to import your first week.',
  },
};

const ORDERED_STEPS: WizardStep[] = [
  'self',
  'uplines',
  'clients',
  'accounts',
  'opening_balances',
];

interface WizardShellProps {
  wizard: WizardState;
  activeStep: WizardStep;
  onStepSelect: (step: WizardStep) => void;
  children: React.ReactNode;
  extra?: React.ReactNode;
}

export function WizardShell({
  wizard,
  activeStep,
  onStepSelect,
  children,
  extra,
}: WizardShellProps) {
  return (
    <div className="min-h-screen bg-bg font-ui text-ink">
      <header className="border-b border-border bg-surface px-6 py-4 shadow-sm">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <span className="text-body font-semibold tracking-tight">
            SRB <span className="text-brand">Accounts</span>
          </span>
          <span className="text-ink-muted">·</span>
          <span className="text-small text-ink-muted">First-run setup</span>
          <Pill tone={wizard.completed ? 'green' : 'amber'}>
            {wizard.completed ? 'setup complete' : 'in progress'}
          </Pill>
        </div>
      </header>

      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 p-4 sm:p-6 md:grid-cols-[260px_1fr]">
        <nav aria-label="Wizard steps" className="space-y-1">
          {ORDERED_STEPS.map((step) => {
            const status = wizard.stepStatus[step];
            const isActive = step === activeStep;
            return (
              <button
                key={step}
                type="button"
                onClick={() => onStepSelect(step)}
                className={[
                  'flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors',
                  isActive
                    ? 'border-brand/30 bg-brand-light'
                    : 'border-border hover:bg-surface-raised',
                ].join(' ')}
              >
                <span
                  className={[
                    'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                    status === 'complete'
                      ? 'bg-pos text-white'
                      : isActive
                        ? 'bg-brand text-white'
                        : 'bg-surface-raised text-ink-muted ring-1 ring-border',
                  ].join(' ')}
                >
                  {status === 'complete' ? '✓' : ORDERED_STEPS.indexOf(step) + 1}
                </span>
                <span>
                  <span className="block text-small font-medium text-ink">
                    {STEP_LABELS[step].title}
                  </span>
                  <span className="block text-caption text-ink-muted">
                    {STEP_LABELS[step].description}
                  </span>
                </span>
              </button>
            );
          })}
          {extra}
        </nav>

        <main className="min-w-0">{children}</main>
      </div>
    </div>
  );
}
